import { useState } from 'react';
import { Download } from 'lucide-react';
import { api, ApiClientError } from '../../lib/api.js';
import { TextField } from '../../components/ui/Field.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { todayIso, currentFyStart } from './dateRange.js';

const EXPORTS = [
  {
    path: '/reports/exports/invoices',
    filename: 'invoices.csv',
    label: 'Sales & GST invoices',
    hint: 'Invoice no, customer, GSTIN/PAN, GST breakup, totals — for GSTR-1/3B reconciliation.',
  },
  {
    path: '/reports/exports/gold-purchases',
    filename: 'gold-purchases.csv',
    label: 'Gold purchase (buy-back)',
    hint: 'Bill no, seller, weight, rate, amount, ID proof on file.',
  },
  {
    path: '/reports/exports/repairs',
    filename: 'repair-vouchers.csv',
    label: 'Repair vouchers',
    hint: 'Voucher no, customer, aasari, charges, status.',
  },
  {
    path: '/reports/exports/issue-vouchers',
    filename: 'issue-vouchers.csv',
    label: 'Issue vouchers',
    hint: 'What went out to karigars/aasaris, with weights.',
  },
  {
    path: '/reports/exports/receipt-vouchers',
    filename: 'receipt-vouchers.csv',
    label: 'Receipt vouchers',
    hint: 'What came back against each issue voucher, mismatches flagged.',
  },
  {
    path: '/reports/exports/chit-installments',
    filename: 'chit-installments.csv',
    label: 'Chit installments',
    hint: 'Scheme, customer, installment, amount, payment mode and date.',
  },
  {
    path: '/reports/exports/stock-register',
    filename: 'stock-register.csv',
    label: 'Stock register',
    hint: 'Opening / movements / closing by metal and purity — the same view as the on-screen register.',
  },
];

export default function ExportsPage() {
  const [from, setFrom] = useState(currentFyStart());
  const [to, setTo] = useState(todayIso());
  const [downloadingPath, setDownloadingPath] = useState(null);
  const [error, setError] = useState(null);

  const handleDownload = async (item) => {
    setError(null);
    setDownloadingPath(item.path);
    try {
      const res = await api.raw(item.path, { query: { from, to } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : `Could not export ${item.label}. Please try again.`);
    } finally {
      setDownloadingPath(null);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Export data</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted">
          CSV downloads for your CA — every record in the period, cancelled ones included with their status shown,
          ready to open in Excel or Google Sheets. Amounts and weights are plain numbers, dates are ISO — built for
          reconciliation, not for reading on screen.
        </p>
      </div>

      <div className="card flex flex-wrap items-end gap-3 p-4">
        <TextField label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <TextField label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <p className="pb-2.5 text-xs text-muted">Defaults to the current financial year to date.</p>
      </div>

      {error ? (
        <p className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">{error}</p>
      ) : null}

      <div className="card divide-y divide-border p-1">
        {EXPORTS.map((item) => (
          <div key={item.path} className="flex flex-col gap-2 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium">{item.label}</p>
              <p className="text-xs text-muted">{item.hint}</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              loading={downloadingPath === item.path}
              onClick={() => handleDownload(item)}
              className="self-start sm:self-auto"
            >
              <Download size={16} aria-hidden="true" /> Download CSV
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
